import { useCallback, useEffect, useState } from "react";

function expandBounds(
  bounds: google.maps.LatLngBounds,
  factor = 0.15
): google.maps.LatLngBounds {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const latPad = (ne.lat() - sw.lat()) * factor;
  const lngPad = (ne.lng() - sw.lng()) * factor;

  return new google.maps.LatLngBounds(
    { lat: sw.lat() - latPad, lng: sw.lng() - lngPad },
    { lat: ne.lat() + latPad, lng: ne.lng() + lngPad }
  );
}

/**
 * Same strategy as planejamento operacional: only keep markers whose centroids
 * are inside the padded viewport. Syncs on map `idle` to avoid work mid-gesture.
 */
export function useViewportTalhaoIds(
  mapRef: { current: google.maps.Map | null },
  features: readonly { properties?: Record<string, any> | null }[],
  enabled = true
) {
  const [visibleIds, setVisibleIds] = useState<Set<string> | null>(null);

  const syncVisibleIds = useCallback(() => {
    const map = mapRef.current;
    if (!map || !enabled) {
      setVisibleIds(null);
      return;
    }

    const bounds = map.getBounds();
    if (!bounds) return;

    const padded = expandBounds(bounds);
    const next = new Set<string>();

    for (let i = 0; i < features.length; i++) {
      const props = features[i]?.properties;
      const rawId = props?.id;
      const codigo = props?.codigo;
      const lat = props?.pontoCentralLat;
      const lng = props?.pontoCentralLong;
      if (lat == null || lng == null) continue;
      if (!padded.contains({ lat, lng })) continue;

      // Prefer real ids; empty GUID is shared by many features and must not be the only key.
      const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
      const hasRealId =
        rawId != null &&
        String(rawId) !== "" &&
        String(rawId).toLowerCase() !== EMPTY_GUID;

      if (hasRealId) {
        next.add(String(rawId));
      }
      if (codigo != null && String(codigo) !== "") {
        next.add(String(codigo));
      }
    }

    setVisibleIds((prev) => {
      if (prev && prev.size === next.size) {
        let same = true;
        next.forEach((id) => {
          if (!prev.has(id)) same = false;
        });
        if (same) return prev;
      }
      return next;
    });
  }, [mapRef, features, enabled]);

  useEffect(() => {
    if (!enabled) {
      setVisibleIds(null);
      return;
    }

    const map = mapRef.current;
    if (!map) return;

    syncVisibleIds();
    const listener = map.addListener("idle", syncVisibleIds);
    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [mapRef, enabled, syncVisibleIds, features]);

  return visibleIds;
}

export function useViewportStationKeys(
  mapRef: { current: google.maps.Map | null },
  stations: readonly {
    key: string;
    lat: number;
    lng: number;
  }[],
  enabled = true
) {
  const [visibleKeys, setVisibleKeys] = useState<Set<string> | null>(null);

  const syncVisibleKeys = useCallback(() => {
    const map = mapRef.current;
    if (!map || !enabled) {
      setVisibleKeys(null);
      return;
    }

    const bounds = map.getBounds();
    if (!bounds) return;

    const padded = expandBounds(bounds);
    const next = new Set<string>();

    for (let i = 0; i < stations.length; i++) {
      const station = stations[i];
      if (padded.contains({ lat: station.lat, lng: station.lng })) {
        next.add(station.key);
      }
    }

    setVisibleKeys((prev) => {
      if (prev && prev.size === next.size) {
        let same = true;
        next.forEach((key) => {
          if (!prev.has(key)) same = false;
        });
        if (same) return prev;
      }
      return next;
    });
  }, [mapRef, stations, enabled]);

  useEffect(() => {
    if (!enabled) {
      setVisibleKeys(null);
      return;
    }

    const map = mapRef.current;
    if (!map) return;

    syncVisibleKeys();
    const listener = map.addListener("idle", syncVisibleKeys);
    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [mapRef, enabled, syncVisibleKeys, stations]);

  return visibleKeys;
}
